import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { BlockType } from '@next-space/fe-api-idl';
import type { ColorInfo } from 'src/components/color-picker';
import { ColorPicker } from 'src/components/color-picker';
import {
  createColorPickerData,
  DEFAULT_COLOR_PICKER_DATA,
} from 'src/components/color-picker/default-data';
import { useSelectedBlockHistory } from 'src/hooks/page/use-selected-block-history';
import { useTransaction } from 'src/hooks/use-transaction';
import { updateBlock } from 'src/redux/managers/block/update';
import { cache } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { bizTracker } from 'src/utils/biz-tracker';
import { usePickBlock } from 'src/utils/pick-block';
import { selectedBlocksToIds } from 'src/utils/select-block-util';
import { saveLastMarkBlockInfo } from '../../utils/mark-block-utils';
import { removeColorFromSegments } from '../../utils/segments';
import { covertBlockToTitle } from '../menu-list/menu-item';

interface Props {
  items?: typeof DEFAULT_COLOR_PICKER_DATA;
  blockId: string;
  onCloseModal: () => void;
  onColorSelect?: (colorInfo: ColorInfo) => void;
  disabledHasRecently?: boolean;
}

/**
 * 清除块的颜色设置和 segments 中的颜色设置
 * @param blockId 块 ID
 * @param clearBlockColor 是否清除块级别的颜色（子块需要清除，父块不需要因为会设置新颜色）
 */
const clearBlockColorSettings = (blockId: string, clearBlockColor: boolean) => {
  const block = cache.blocks[blockId];
  if (!block) return;

  const updates: Partial<NextBlock> = {};
  let needUpdate = false;

  // 清除块级别的颜色
  if (clearBlockColor) {
    if (block.textColor) {
      updates.textColor = '';
      needUpdate = true;
    }
    if (block.backgroundColor) {
      updates.backgroundColor = '';
      needUpdate = true;
    }
  }

  // 清除 segments 中的颜色
  const newSegments = removeColorFromSegments(block.data?.segments);
  if (newSegments !== block.data?.segments) {
    updates.data = { ...block.data, segments: newSegments };
    needUpdate = true;
  }

  if (needUpdate) {
    updateBlock(blockId, updates);
  }
};

/**
 * 判断块类型是否应该被排除（不递归处理颜色）
 * 页面类型块不应该继承父块颜色，避免递归层级过深
 */
const EXCLUDED_BLOCK_TYPES = new Set([
  BlockType.PAGE,
  BlockType.FOLDER,
  BlockType.COLLECTION_VIEW_PAGE,
  BlockType.COLLECTION_VIEW,
  BlockType.REFERENCE_COLLECTION,
  BlockType.REFERENCE_COLLECTION_PAGE,
  BlockType.MIND_MAPPING,
  BlockType.MIND_MAPPING_PAGE,
]);

/**
 * 递归清除子块的颜色设置
 * 只处理非页面类型的块，避免递归到嵌入的页面或子页面
 * @param blockId 父块 ID
 */
const clearChildrenColorSettings = (blockId: string) => {
  const block = cache.blocks[blockId];
  if (!block || !block.subNodes || block.subNodes.length === 0) return;

  for (const childId of block.subNodes) {
    const childBlock = cache.blocks[childId];
    if (!childBlock) continue;

    // 排除页面类型的块，不处理其颜色
    if (EXCLUDED_BLOCK_TYPES.has(childBlock.type)) continue;

    // 清除子块的颜色（包括块级别和 segments 级别）
    clearBlockColorSettings(childId, true);
    // 递归处理子块的子块
    clearChildrenColorSettings(childId);
  }
};

export const ColorPickerModule = (props: Props) => {
  const { t } = useAppTranslation();
  const { onCloseModal, blockId, items, disabledHasRecently, onColorSelect } = props;
  const block = usePickBlock(blockId, []);
  const { selectedBlockHistory, selectedBlock } = useSelectedBlockHistory();

  const transaction = useTransaction();

  return (
    <ColorPicker
      disabledHasRecently={disabledHasRecently}
      items={items || createColorPickerData(t as any).DEFAULT_COLOR_PICKER_DATA}
      onColorSelect={(colorInfo) => {
        onColorSelect?.(colorInfo);
        const ids = selectedBlock.length ? selectedBlocksToIds(selectedBlock) : [blockId];

        bizTracker.event('color', {
          block_name: covertBlockToTitle(blockId),
          is_background: colorInfo.isBgColor,
        });

        transaction(() => {
          ids.forEach((id) => {
            // 1. 清除当前块 segments 中的颜色设置（使富文本继承块颜色）
            clearBlockColorSettings(id, false);

            // 2. 递归清除所有子块的颜色设置（使子块继承父块颜色）
            clearChildrenColorSettings(id);

            // 3. 设置当前块的新颜色
            updateBlock(id, {
              backgroundColor: colorInfo.isBgColor ? colorInfo.colorkey : '',
              textColor: colorInfo.isBgColor ? '' : colorInfo.colorkey,
            });
          });
          selectedBlockHistory([]);
        });

        onCloseModal();

        if (block?.type === BlockType.MARK) {
          saveLastMarkBlockInfo({
            backgroundColor: colorInfo.isBgColor ? colorInfo.colorkey : '',
            textColor: colorInfo.isBgColor ? '' : colorInfo.colorkey,
          });
        }
      }}
    />
  );
};
